import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UserService } from '../../services/user.service';

@Component({
  selector: 'app-activation',
  templateUrl: './activation.component.html',
  styleUrls: ['./activation.component.scss']
})
export class ActivationComponent implements OnInit {
  email = '';
  name = '';
  loading = false;
  tokenRequested = false;
  token = '';
  password = '';
  confirmPassword = '';
  passwordStep = false;
  activationCompleted = false;
  message = '';
  error = '';
  countdown: any;
  sessionToken = '';

  constructor(
    private route: ActivatedRoute,
    private userService: UserService,
    private router: Router
  ) {}

  ngOnInit(): void {
    const encoded = this.route.snapshot.queryParamMap.get('key');
    const fallbackEmail = this.route.snapshot.queryParamMap.get('email');
    const tokenFromLink = String(this.route.snapshot.queryParamMap.get('token') || '')
      .replace(/\D+/g, '')
      .slice(0, 6);
    this.email = encoded ? this.decodeEmail(encoded) : (fallbackEmail || '');
    if (tokenFromLink.length === 6) {
      this.tokenRequested = true;
      this.token = tokenFromLink;
      this.message = 'Link de redefinição detectado. Valide o código para continuar.';
    }
    if (this.email) {
      this.userService.getActivationInfo(this.email).subscribe({
        next: (info: any) => {
          this.name = info?.name || '';
          if (info?.active) {
            this.message = 'Este usuário já está ativo. Acesse a tela de login.';
          }
        },
        error: () => {}
      });
    }
  }

  private decodeEmail(value: string): string {
    try {
      const base64 = decodeURIComponent(value);
      const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
      const binary = atob(padded);
      let result = '';
      for (let i = 0; i < binary.length; i++) {
        const code = binary.charCodeAt(i).toString(16).padStart(2, '0');
        result += `%${code}`;
      }
      return decodeURIComponent(result);
    } catch {
      return '';
    }
  }

  requestToken(): void {
    if (!this.email) {
      this.error = 'E-mail inválido.';
      return;
    }

    this.loading = true;
    this.error = '';

    this.userService.requestActivation(this.email).subscribe({
      next: () => {
        this.loading = false;
        this.tokenRequested = true;
        this.passwordStep = false;
        this.sessionToken = '';
        this.token = '';
        this.message = 'Enviamos o token para o seu e-mail.';
      },
      error: (err) => {
        this.loading = false;
        this.error = err?.error?.error || 'Não foi possível enviar o token.';
      }
    });
  }

  goToPasswordStep(): void {
    if (this.token.length !== 6) {
      this.error = 'Informe o token com 6 dígitos.';
      return;
    }

    this.loading = true;
    this.error = '';

    this.userService.validateActivationToken(this.email, this.token).subscribe({
      next: (result) => {
        this.loading = false;
        this.sessionToken = result.sessionToken;
        this.passwordStep = true;
        this.activationCompleted = false;
        this.message = 'Token validado! Agora defina sua senha.';
      },
      error: (err) => {
        this.loading = false;
        this.error = err?.error?.error || 'Token inválido ou expirado.';
      }
    });
  }

  activateAccount(): void {
    if (!this.email || !this.sessionToken) {
      this.error = 'Token não validado.';
      return;
    }

    if (!this.isPasswordStrong(this.password)) {
      this.error = 'A senha precisa ter no mínimo 8 caracteres, com letra maiúscula, minúscula e número.';
      return;
    }

    if (!this.password || this.password !== this.confirmPassword) {
      this.error = 'As senhas não conferem.';
      return;
    }

    this.loading = true;
    this.error = '';

    this.userService.completeActivation({ email: this.email, token: this.sessionToken, password: this.password }).subscribe({
      next: () => {
        this.loading = false;
        this.activationCompleted = true;
        this.passwordStep = false;
        this.message = 'Conta ativada com sucesso! Redirecionando para o login...';
        setTimeout(() => this.router.navigate(['/login']), 2500);
      },
      error: (err) => {
        this.loading = false;
        this.error = err?.error?.error || 'Não foi possível ativar a conta.';
      }
    });
  }

  private isPasswordStrong(value: string): boolean {
    if (!value || value.length < 8) {
      return false;
    }
    const hasUpper = /[A-Z]/.test(value);
    const hasLower = /[a-z]/.test(value);
    const hasNumber = /[0-9]/.test(value);
    return hasUpper && hasLower && hasNumber;
  }
}
